import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SSO_ERRORS = {
  netlen_id_reddetti: "Netlen ID girişi reddedildi.",
  eksik_parametre: "Giriş tamamlanamadı (eksik parametre).",
  oturum_suresi_doldu: "Giriş oturumu zaman aşımına uğradı, tekrar deneyin.",
  gecersiz_durum: "Giriş doğrulaması geçersiz, tekrar deneyin.",
  state_uyusmuyor: "Güvenlik doğrulaması başarısız, tekrar deneyin.",
  dogrulama_hatasi: "Netlen ID kimlik doğrulaması başarısız.",
  email_yok: "Netlen ID hesabında e-posta bulunamadı.",
  hesap_pasif: "Hesabınız devre dışı. Yöneticinizle görüşün.",
};

export default function Login() {
  const { login } = useAuth();
  const branding = useBranding();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  useEffect(() => {
    // Netlen ID dönüşü: başarı #sso_token=..., hata ?sso_error=...
    const tokenMatch = (window.location.hash || "").match(/sso_token=([^&]+)/);
    if (tokenMatch) {
      try { localStorage.setItem("cd_token", decodeURIComponent(tokenMatch[1])); } catch {}
      window.location.replace("/inbox");
      return;
    }
    const err = new URLSearchParams(window.location.search).get("sso_error");
    if (err) {
      setError(SSO_ERRORS[err] || "Netlen ID ile giriş başarısız.");
      window.history.replaceState({}, "", "/login");
    }
    axios.get(`${API}/auth/oidc/status`)
      .then(({ data }) => setSsoEnabled(!!data.enabled))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
    } catch (err) {
      setError(err?.response?.data?.detail || "Geçersiz e-posta veya şifre");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sol panel — marka */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] flex-shrink-0 p-10"
        style={{ background: "#0f172a" }}>
        <div className="flex items-center gap-3">
          <img src={branding.logo_url} alt={branding.brand_name} className="w-9 h-9 rounded-xl object-cover" />
          <span className="text-white font-semibold text-base tracking-tight">{branding.brand_name}</span>
        </div>

        <div>
          <div>
            <p className="text-white text-lg font-semibold leading-snug mb-2">
              {branding.login_subtitle}
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Ziyaretçilerinizle anlık iletişim kurun, yapay zeka ile 7/24 otomatik yanıt verin. Tüm kanallarınızı tek panelden yönetin.
            </p>
          </div>
        </div>

        <div className="text-slate-600 text-xs">{branding.footer_text}</div>
      </div>

      {/* Sağ panel — form */}
      <div className="flex-1 flex items-center justify-center bg-white p-6">
        <div className="w-full max-w-[380px]">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <img src={branding.logo_url} alt={branding.brand_name} className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-semibold text-slate-800">{branding.brand_name}</span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Giriş yapın</h1>
          <p className="text-slate-500 text-sm mb-8">Operatör panelinize hoş geldiniz</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">E-posta</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full h-11 rounded-lg border border-slate-200 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all bg-white text-slate-900 placeholder-slate-400"
                placeholder="ornek@sirket.com"
                required
                autoFocus
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Şifre</label>
              <div className="relative">
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full h-11 rounded-lg border border-slate-200 px-4 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all bg-white text-slate-900"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 mt-1"
              data-testid="auth-submit-btn"
            >
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>

          {ssoEnabled && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400">veya</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
              <button
                type="button"
                onClick={() => { window.location.href = `${API}/auth/oidc/login`; }}
                className="w-full h-11 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                data-testid="netlen-id-login-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Netlen ID ile giriş
              </button>
            </>
          )}

          <p className="mt-6 text-center text-xs text-slate-400">
            Hesap oluşturmak için yöneticinizle iletişime geçin.
          </p>
        </div>
      </div>
    </div>
  );
}
